import type { Tree, TreePerson } from './tree'

// The people in a tree, sorted into families for the People page's cards and list. A family is a couple or a parent
// with their children, read straight from the tree's parent and partner links; nothing is stored. Everyone shows up
// once: in the family they head if they have a partner or children, otherwise in the one they grew up in. Pets live
// with their people, after them.
// The People page shows everyone you're connected to as a list, your circles like albums of faces, or the family tree.
export type PeopleView = 'connections' | 'circles' | 'tree'
export const PEOPLE_VIEWS: PeopleView[] = ['connections', 'circles', 'tree']

export type PeopleGroup = { key: string; name: string; people: TreePerson[] }

type Unit = { parents: string[]; children: string[] }

const words = (name: string) => name.trim().split(/\s+/)
// A last name only when there's more than one word: "Aunt Brenda" or "June" has none we can trust.
const surname = (name: string) => {
  const w = words(name)
  return w.length > 1 ? w.at(-1)! : null
}
const unitKey = (parents: string[]) => [...parents].sort().join('+')
const byName = (a: TreePerson, b: TreePerson) => a.name.localeCompare(b.name)

export function familyGroups(tree: Tree): PeopleGroup[] {
  const byId = new Map(tree.people.map((p) => [p.id, p]))
  const links = tree.links.filter((l) => byId.has(l.a) && byId.has(l.b))
  const parentsOf = new Map<string, string[]>()
  const partnersOf = new Map<string, string[]>()
  const siblingsOf = new Map<string, string[]>()
  const push = (map: Map<string, string[]>, key: string, value: string) =>
    map.set(key, [...(map.get(key) ?? []), value])
  for (const l of links) {
    if (l.kind === 'parent') push(parentsOf, l.b, l.a)
    const twoWay = l.kind === 'partner' ? partnersOf : l.kind === 'sibling' ? siblingsOf : null
    if (twoWay) {
      push(twoWay, l.a, l.b)
      push(twoWay, l.b, l.a)
    }
  }

  const units = new Map<string, Unit>()
  const unit = (parents: string[]) => {
    const key = unitKey(parents)
    if (!units.has(key)) units.set(key, { parents: [...parents], children: [] })
    return key
  }
  // Families often record only one parent. When that parent has exactly one partner, the child sits with the couple.
  const grewUpIn = new Map<string, string>()
  for (const [child, parents] of parentsOf) {
    const partners = parents.length === 1 ? (partnersOf.get(parents[0]!) ?? []) : []
    const key = unit(partners.length === 1 ? [parents[0]!, partners[0]!] : parents)
    units.get(key)!.children.push(child)
    grewUpIn.set(child, key)
  }
  for (const l of links) if (l.kind === 'partner') unit([l.a, l.b])

  // Where each person goes: the family they head (the one with the most children, if there are several), else the
  // one they grew up in, else the one a brother or sister grew up in.
  const placed = new Map<string, string>()
  const heads = [...units].sort(([ka, a], [kb, b]) => b.children.length - a.children.length || ka.localeCompare(kb))
  for (const [key, u] of heads) for (const p of u.parents) if (!placed.has(p)) placed.set(p, key)
  for (const [child, key] of grewUpIn) if (!placed.has(child)) placed.set(child, key)
  for (let changed = true; changed;) {
    changed = false
    for (const p of tree.people) {
      if (placed.has(p.id)) continue
      const home = (siblingsOf.get(p.id) ?? []).map((s) => grewUpIn.get(s)).find(Boolean)
      if (home) {
        placed.set(p.id, home)
        grewUpIn.set(p.id, home)
        changed = true
      }
    }
  }

  // A pet goes where its first pet parent is, and someone with pets but no other family on the tree heads their own.
  for (const l of links) {
    if (l.kind !== 'pet' || placed.has(l.b)) continue
    if (!placed.has(l.a)) placed.set(l.a, unit([l.a]))
    placed.set(l.b, placed.get(l.a)!)
  }

  const members = new Map<string, TreePerson[]>()
  for (const [id, key] of placed) members.set(key, [...(members.get(key) ?? []), byId.get(id)!])

  // Closest families first, counting steps out from you along any kind of link.
  const distance = new Map<string, number>()
  if (tree.me && byId.has(tree.me)) {
    distance.set(tree.me, 0)
    const queue = [tree.me]
    while (queue.length) {
      const at = queue.shift()!
      for (const l of links) {
        const next = l.a === at ? l.b : l.b === at ? l.a : null
        if (next && !distance.has(next)) {
          distance.set(next, distance.get(at)! + 1)
          queue.push(next)
        }
      }
    }
  }
  const near = (people: TreePerson[]) => Math.min(...people.map((p) => distance.get(p.id) ?? Infinity))

  const born = (p: TreePerson) => p.born ?? '9999'
  const youFirst = (a: TreePerson, b: TreePerson) => Number(b.id === tree.me) - Number(a.id === tree.me) || byName(a, b)
  const families = [...members].map(([key, people]) => {
    const u = units.get(key)!
    const parents = u.parents.map((id) => byId.get(id)!).sort(youFirst)
    const rest = people.filter((p) => !u.parents.includes(p.id))
    rest.sort((a, b) => Number(a.pet) - Number(b.pet) || born(a).localeCompare(born(b)) || byName(a, b))
    return { key, parents, people: [...parents.filter((p) => placed.get(p.id) === key), ...rest] }
  })

  // "The Rivera family" when the parents share a last name, unless two families would get the same name; then
  // "Rose and Sam Rivera". Parents with different or unknown last names are named in full.
  const shared = (parents: TreePerson[]) => {
    const names = parents.map((p) => surname(p.name))
    return names[0] && names.every((n) => n === names[0]) ? names[0] : null
  }
  const taken = new Map<string, number>()
  for (const f of families) {
    const s = shared(f.parents)
    if (s) taken.set(s, (taken.get(s) ?? 0) + 1)
  }
  const and = (list: string[]) =>
    list.length <= 1 ? (list[0] ?? '') : `${list.slice(0, -1).join(', ')} and ${list.at(-1)}`
  const named = families.map((f) => {
    const s = shared(f.parents)
    let name: string
    if (s && taken.get(s) === 1) name = `The ${s} family`
    else if (s) name = `${and(f.parents.map((p) => words(p.name).slice(0, -1).join(' ')))} ${s}`
    else name = and(f.parents.map((p) => p.name))
    return { key: f.key, name, people: f.people }
  })
  named.sort((a, b) => near(a.people) - near(b.people) || a.name.localeCompare(b.name))

  // Whoever isn't in a family yet: relatives whose families aren't on the tree, family friends, and people not
  // linked to anyone.
  const left = tree.people.filter((p) => !placed.has(p.id)).sort(byName)
  const linked = (p: TreePerson) => links.some((l) => l.a === p.id || l.b === p.id)
  const friends = left.filter(
    (p) => linked(p) && links.every((l) => (l.a !== p.id && l.b !== p.id) || l.kind === 'friend'),
  )
  const more = left.filter((p) => linked(p) && !friends.includes(p))
  const loose = left.filter((p) => !linked(p))
  const extra: PeopleGroup[] = [
    { key: 'more', name: 'More family', people: more },
    { key: 'friends', name: 'Family friends', people: friends },
    { key: 'loose', name: 'Not linked to anyone yet', people: loose },
  ]
  return [...named, ...extra.filter((g) => g.people.length > 0)]
}
